// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import Experience from 'app/common/react/components/experiences/Experience/Experience';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { State as ReduxState } from 'app/common/redux/types';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import { updateProfileBoardView } from 'app/common/redux/actions/views';
import { Box, Flyout, Layer, Icon, Text, SegmentedControl } from 'gestalt';

type ReduxProps = {|
  profileBoardView: ?('square' | 'list' | 'wide'),
|};

type Props = {|
  ...ReduxProps,
  dispatch: Function,
  metadata: {
    data: {
      profile_board_rep_view_type: null | 'list' | 'square' | 'wide',
    },
  },
|};

/**
 * @cvuerings TODO replace these with Gestalt Icon once they've landed
 */
const icons = {
  square:
    'M16.00,20.00 L20.00,20.00 L20.00,16.00 L16.00,16.00 L16.00,20.00 Z M10.00,8.00 L10.00,4.00 L14.00,4.00 L14.00,8.00 L10.00,8.00 Z M4.00,8.00 L4.00,4.00 L8.00,4.00 L8.00,8.00 L4.00,8.00 Z M16.00,8.00 L16.00,4.00 L20.00,4.00 L20.00,8.00 L16.00,8.00 Z M4.00,14.00 L4.00,10.00 L8.00,10.00 L8.00,14.00 L4.00,14.00 Z M10.00,14.00 L10.00,10.00 L14.00,10.00 L14.00,14.00 L10.00,14.00 Z M16.00,14.00 L16.00,10.00 L20.00,10.00 L20.00,14.00 L16.00,14.00 Z M4.00,20.00 L4.00,16.00 L8.00,16.00 L8.00,20.00 L4.00,20.00 Z M10.00,20.00 L10.00,16.00 L14.00,16.00 L14.00,20.00 L10.00,20.00 Z',
  list:
    'M4.00,13.00 L4.00,11.00 L20.00,11.00 L20.00,13.00 L4.00,13.00 Z M4.00,8.00 L4.00,6.00 L20.00,6.00 L20.00,8.00 L4.00,8.00 Z M4.00,18.00 L4.00,16.00 L20.00,16.00 L20.00,18.00 L4.00,18.00 Z',
  wide:
    'M13.00,20.00 L13.00,13.00 L20.00,13.00 L20.00,20.00 L13.00,20.00 Z M4.00,11.00 L4.00,4.00 L11.00,4.00 L11.00,11.00 L4.00,11.00 Z M13.00,11.00 L13.00,4.00 L20.00,4.00 L20.00,11.00 L13.00,11.00 Z M4.00,20.00 L4.00,13.00 L11.00,13.00 L11.00,20.00 L4.00,20.00 Z',
};

export class ProfileBoardViewSelector extends Component<Props> {
  componentDidUpdate() {
    const { dispatch, metadata, profileBoardView } = this.props;

    // Dispatch the correct action depending on a certain state.
    // 1. If we have a saved state in the url meta store, use that
    // 2. If no saved state, select wide as the default
    if (metadata && metadata.data && !profileBoardView) {
      const { profile_board_rep_view_type: profileBoardRepViewType } = metadata.data;
      if (profileBoardRepViewType) {
        dispatch(updateProfileBoardView(profileBoardRepViewType));
      } else {
        dispatch(updateProfileBoardView('wide'));
      }
    }
  }

  anchor: ?HTMLElement;
  completeViewTypeExperience: ?Function;

  handleViewChange = ({ activeIndex }: { activeIndex: number }) => {
    const { dispatch } = this.props;

    if (this.completeViewTypeExperience) {
      this.completeViewTypeExperience();
    }

    const selectedItem = this.options.find((option, index) => index === activeIndex) || {};
    const { value } = selectedItem;

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.PROFILE_BOARD_REP_VIEW_TYPE_TOGGLE,
      viewParameter: PCONST.thrift.ViewParameterType.USER_SELF,
      element: this.logElementType(value),
    });

    const resource = ResourceFactory.create('UserMetadataResource', {
      profile_board_rep_view_type: value,
    });
    resource.callUpdate();
    dispatch(updateProfileBoardView(value));
  };

  logElementType(value: 'square' | 'list' | 'wide') {
    switch (value) {
      case 'list':
        return PCONST.thrift.ElementType.PROFILE_BOARD_REP_VIEW_LIST_OPTION;
      case 'wide':
        return PCONST.thrift.ElementType.PROFILE_BOARD_REP_VIEW_WIDE_OPTION;
      case 'square':
        return PCONST.thrift.ElementType.PROFILE_BOARD_REP_VIEW_SQUARE_OPTION;
      default:
        return '';
    }
  }

  options: Array<{|
    label: string,
    value: 'square' | 'list' | 'wide',
  |}> = [
    {
      label: i18n._('Wide view', 'board view type option: wide'),
      value: 'wide',
    },
    {
      label: i18n._('square view', 'board view type option: square'),
      value: 'square',
    },
    {
      label: i18n._('List view', 'board view type option: list'),
      value: 'list',
    },
  ];

  render() {
    const { metadata, profileBoardView } = this.props;

    // Wait until the metadata resource has loaded
    if (!metadata || !metadata.data) {
      return null;
    }

    const selectedIndex = this.options.findIndex((option) => option.value === profileBoardView);

    return (
      <React.Fragment>
        <Box display="flex" alignItems="center">
          <div
            ref={(el) => {
              this.anchor = el;
            }}
          >
            <Box paddingX={3}>
              <SegmentedControl
                items={this.options.map((option, index) => (
                  <Box>
                    <Icon
                      accessibilityLabel={option.label}
                      color={index === selectedIndex ? 'darkGray' : 'gray'}
                      dangerouslySetSvgPath={{ __path: icons[option.value] }}
                      size={24}
                    />
                  </Box>
                ))}
                selectedItemIndex={selectedIndex}
                onChange={this.handleViewChange}
                responsive
              />
            </Box>
          </div>
        </Box>
        <Experience
          placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER}
          experienceIds={[PCONST.thrift.Experience.WEB_PROFILE_BOARD_VIEWTYPE_TOGGLE_EDUCATION]}
        >
          {({ complete, dismiss }) => {
            this.completeViewTypeExperience = complete;
            return (
              <Layer>
                <Flyout
                  anchor={this.anchor}
                  color="darkGray"
                  idealDirection="down"
                  onDismiss={dismiss}
                  positionRelativeToAnchor={false}
                  shouldFocus={false}
                  size="md"
                >
                  <Box column={12} padding={3}>
                    <Text bold color="white" size="md">
                      {i18n._(
                        "New! Change how you see your boards so it's easier to find what you're looking for",
                        'Label to introduce the board view type toggle',
                      )}
                    </Text>
                  </Box>
                </Flyout>
              </Layer>
            );
          }}
        </Experience>
      </React.Fragment>
    );
  }
}

export default compose(
  connect<_, {||}, _, _, _, _>(
    (state: ReduxState): ReduxProps => ({
      profileBoardView: state.ui.views && state.ui.views.profileBoardView,
    }),
  ),
  withViewer,
  withResource({
    name: 'UserMetadataResource',
    key: 'metadata',
    options: (props) => ({
      user: {
        id: props.viewer.username,
      },
    }),
  }),
)(ProfileBoardViewSelector);
